import type { SeriesScope } from '@secretario/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PagePanel } from '../../components/ui/PagePanel.js';
import { describeError } from '../../lib/api/http.js';
import {
  cancelReminder,
  createAppointment,
  createReminder,
  deleteAppointment,
  updateAppointment,
} from '../../lib/api/personal.js';
import { apiFetch } from '../../lib/api/http.js';
import {
  absoluteLabel,
  ALERT_CHOICES,
  buildRRule,
  dateOfKey,
  dayKey,
  readRRule,
  REPEAT_CHOICES,
  repeatLabel,
  type AgendaEntry,
  type RepeatChoice,
} from '../../lib/agendaView.js';

export type SheetTarget =
  | { mode: 'new'; date: string; kind: 'appointment' | 'reminder' }
  | { mode: 'edit'; entry: AgendaEntry };

/** O que foi apagado, para o "Desfazer" da página. */
export type Removed =
  | { kind: 'appointment'; id: string; occurrenceStart?: string; label: string }
  | { kind: 'reminder'; id: string; label: string };

const WEEKDAY_LETTERS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
const WEEKDAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const pad = (value: number) => String(value).padStart(2, '0');
const timeOf = (date: Date) => `${pad(date.getHours())}:${pad(date.getMinutes())}`;
const deviceZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
};

function nextHour(): string {
  const now = new Date();
  return `${pad(Math.min(23, now.getHours() + 1))}:00`;
}

/**
 * Criar ou editar um compromisso ou lembrete. Antes de salvar, a data aparece
 * por extenso, com o dia da semana, calculada pelo app (nunca pelo modelo).
 * Numa série, a pessoa escolhe: só este ou este e os próximos.
 */
export function EventSheet({
  target,
  onClose,
  onSaved,
  onRemoved,
}: {
  target: SheetTarget | null;
  onClose: () => void;
  onSaved: (message: string) => void;
  onRemoved: (removed: Removed) => void;
}) {
  const editing = target?.mode === 'edit' ? target.entry : null;
  const appointment = editing?.appointment;
  const seriesId = appointment?.seriesId ?? (appointment?.rrule ? appointment.id : null);

  const [kind, setKind] = useState<'appointment' | 'reminder'>('appointment');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(dayKey(new Date()));
  const [time, setTime] = useState('09:00');
  const [endTime, setEndTime] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [repeat, setRepeat] = useState<RepeatChoice | 'custom'>('none');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [alerts, setAlerts] = useState<number[]>([10]);
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [scope, setScope] = useState<SeriesScope>('this');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // A regra da série mora no compromisso que a define: busca para preencher a repetição.
  const seriesQ = useQuery({
    queryKey: ['agenda-series', seriesId],
    queryFn: () => apiFetch<{ rrule?: string | null }>(`/v1/agenda/appointments/${seriesId}`),
    enabled: !!seriesId && target?.mode === 'edit',
    staleTime: 10_000,
  });
  const storedRule = appointment?.rrule ?? seriesQ.data?.rrule ?? null;

  useEffect(() => {
    if (!target) return;
    setError('');
    setBusy(false);
    setScope('this');
    if (target.mode === 'new') {
      setKind(target.kind);
      setTitle('');
      setDate(target.date);
      // Hoje: a próxima hora cheia. Outro dia: de manhã.
      setTime(target.date === dayKey(new Date()) ? nextHour() : '09:00');
      setEndTime('');
      setAllDay(false);
      setRepeat('none');
      setWeekdays([dateOfKey(target.date).getDay()]);
      setAlerts([10]);
      setLocation('');
      setNotes('');
      return;
    }
    const entry = target.entry;
    setKind(entry.kind);
    setTitle(entry.title);
    setDate(dayKey(entry.start));
    setTime(timeOf(entry.start));
    setEndTime(entry.end && !entry.allDay ? timeOf(entry.end) : '');
    setAllDay(entry.allDay);
    setAlerts(entry.appointment?.alerts ?? []);
    setLocation(entry.appointment?.location ?? '');
    setNotes(entry.appointment?.notes ?? '');
  }, [target]);

  useEffect(() => {
    if (target?.mode !== 'edit') return;
    const read = readRRule(storedRule);
    setRepeat(read.choice);
    setWeekdays(read.weekdays.length ? read.weekdays : [target.entry.start.getDay()]);
  }, [target, storedRule]);

  const start = allDay ? dateOfKey(date) : new Date(`${date}T${time || '09:00'}:00`);
  const valid = title.trim().length > 0 && !Number.isNaN(start.getTime());
  const rule = repeat === 'custom' ? storedRule : buildRRule(repeat, weekdays);
  const confirm = valid
    ? `${kind === 'reminder' ? 'Eu te lembro' : 'Fica marcado para'} ${absoluteLabel(start, allDay)}${
        kind === 'appointment' && rule ? `. ${repeatLabel(rule)}` : ''
      }.`
    : '';

  function toggle<T>(list: T[], value: T): T[] {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  async function save() {
    if (!valid || busy) return;
    setBusy(true);
    setError('');
    try {
      if (kind === 'reminder') {
        const zone = deviceZone();
        await createReminder({
          message: title.trim(),
          date,
          time,
          ...(zone ? { timezone: zone } : {}),
        });
        // Lembrete não tem edição no servidor: o novo entra e o antigo sai.
        if (editing?.reminder) await cancelReminder(editing.reminder.id);
        onSaved(`Lembrete anotado para ${absoluteLabel(start, false)}.`);
        return;
      }
      let end: Date | null = null;
      if (allDay) end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
      else if (endTime) {
        end = new Date(`${date}T${endTime}:00`);
        if (end <= start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
      }
      const zone = deviceZone();
      const common = {
        title: title.trim(),
        startsAt: start.toISOString(),
        allDay,
        alerts: [...alerts].sort((a, b) => a - b),
      };
      if (!appointment) {
        await createAppointment({
          ...common,
          ...(end ? { endsAt: end.toISOString() } : {}),
          ...(zone ? { timezone: zone } : {}),
          ...(rule ? { rrule: rule } : {}),
          ...(location.trim() ? { location: location.trim() } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
      } else {
        const inSeries = !!seriesId;
        await updateAppointment(seriesId ?? appointment.id, {
          ...common,
          endsAt: end ? end.toISOString() : null,
          location: location.trim() || null,
          notes: notes.trim() || null,
          // "Só este" não muda a regra da série; os demais casos levam a regra da tela.
          ...(inSeries && scope === 'this' ? {} : repeat === 'custom' ? {} : { rrule: rule }),
          ...(inSeries
            ? { scope, occurrenceStart: appointment.occurrenceStart ?? appointment.startsAt }
            : {}),
        });
      }
      onSaved(`Marcado para ${absoluteLabel(start, allDay)}.`);
    } catch (failure) {
      setError(describeError(failure, 'Não consegui salvar agora. Tente de novo.'));
      setBusy(false);
    }
  }

  async function remove() {
    if (!editing || busy) return;
    setBusy(true);
    setError('');
    try {
      if (editing.reminder) {
        await cancelReminder(editing.reminder.id);
        onRemoved({ kind: 'reminder', id: editing.reminder.id, label: editing.title });
        return;
      }
      if (!appointment) return;
      const occurrenceStart = appointment.occurrenceStart ?? appointment.startsAt;
      if (seriesId) {
        await deleteAppointment(seriesId, { scope, occurrenceStart });
        onRemoved({
          kind: 'appointment',
          id: seriesId,
          label: editing.title,
          ...(scope === 'this' ? { occurrenceStart } : {}),
        });
      } else {
        await deleteAppointment(appointment.id);
        onRemoved({ kind: 'appointment', id: appointment.id, label: editing.title });
      }
    } catch (failure) {
      setError(describeError(failure, 'Não consegui apagar agora. Tente de novo.'));
      setBusy(false);
    }
  }

  const heading = editing
    ? kind === 'reminder'
      ? 'Editar lembrete'
      : 'Editar compromisso'
    : kind === 'reminder'
      ? 'Novo lembrete'
      : 'Novo compromisso';

  return (
    <PagePanel
      open={!!target}
      kicker="Agenda"
      title={heading}
      onClose={onClose}
      tall
      footer={
        <>
          {editing && (
            <button className="stg-btn agv-danger" onClick={() => void remove()} disabled={busy}>
              Apagar
            </button>
          )}
          <button className="stg-btn solid" onClick={() => void save()} disabled={!valid || busy}>
            {busy ? 'Salvando' : 'Salvar'}
          </button>
        </>
      }
    >
      <form
        className="agv-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {!editing && (
          <div className="stg-seg agv-kind" role="group" aria-label="O que você quer criar">
            <button
              type="button"
              className={kind === 'appointment' ? 'on' : ''}
              aria-pressed={kind === 'appointment'}
              onClick={() => setKind('appointment')}
            >
              Compromisso
            </button>
            <button
              type="button"
              className={kind === 'reminder' ? 'on' : ''}
              aria-pressed={kind === 'reminder'}
              onClick={() => setKind('reminder')}
            >
              Lembrete
            </button>
          </div>
        )}

        <label className="agv-field">
          <span>{kind === 'reminder' ? 'Do que eu te lembro' : 'O que é'}</span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={kind === 'reminder' ? 'Ex.: ligar para a mãe' : 'Ex.: dentista'}
            maxLength={kind === 'reminder' ? 200 : 120}
            enterKeyHint="done"
          />
        </label>

        <div className="agv-two">
          <label className="agv-field">
            <span>Dia</span>
            <input type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </label>
          {!allDay && (
            <label className="agv-field">
              <span>Hora</span>
              <input type="time" value={time} onChange={(event) => setTime(event.target.value)} />
            </label>
          )}
        </div>

        {kind === 'appointment' && (
          <>
            <div className="agv-two">
              <label className="agv-switch">
                <input
                  type="checkbox"
                  role="switch"
                  checked={allDay}
                  onChange={(event) => setAllDay(event.target.checked)}
                />
                <i aria-hidden="true" />
                <span>Dia inteiro</span>
              </label>
              {!allDay && (
                <label className="agv-field">
                  <span>
                    Até <small>opcional</small>
                  </span>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(event) => setEndTime(event.target.value)}
                  />
                </label>
              )}
            </div>

            <fieldset className="agv-set">
              <legend>Repete</legend>
              <div className="agv-chips">
                {REPEAT_CHOICES.map((choice) => (
                  <button
                    key={choice.id}
                    type="button"
                    className={repeat === choice.id ? 'on' : ''}
                    aria-pressed={repeat === choice.id}
                    onClick={() => setRepeat(choice.id)}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
              {repeat === 'custom' && (
                <p className="agv-hint">
                  Esta repetição foi combinada com o Secretário e continua como está. Escolha uma
                  opção acima se quiser trocar.
                </p>
              )}
              {repeat === 'weekly' && (
                <div className="agv-days" role="group" aria-label="Dias da semana">
                  {WEEKDAY_LETTERS.map((letter, day) => (
                    <button
                      key={WEEKDAY_NAMES[day]}
                      type="button"
                      className={weekdays.includes(day) ? 'on' : ''}
                      aria-pressed={weekdays.includes(day)}
                      aria-label={WEEKDAY_NAMES[day]}
                      onClick={() => setWeekdays((list) => toggle(list, day))}
                    >
                      {letter}
                    </button>
                  ))}
                </div>
              )}
            </fieldset>

            <fieldset className="agv-set">
              <legend>Avisar</legend>
              <div className="agv-chips">
                {ALERT_CHOICES.map((choice) => (
                  <button
                    key={choice.minutes}
                    type="button"
                    className={alerts.includes(choice.minutes) ? 'on' : ''}
                    aria-pressed={alerts.includes(choice.minutes)}
                    onClick={() => setAlerts((list) => toggle(list, choice.minutes))}
                  >
                    {choice.label}
                  </button>
                ))}
              </div>
            </fieldset>

            <label className="agv-field">
              <span>
                Onde <small>opcional</small>
              </span>
              <input
                value={location}
                onChange={(event) => setLocation(event.target.value)}
                placeholder="Endereço ou lugar"
                maxLength={200}
              />
            </label>
            <label className="agv-field">
              <span>
                Anotação <small>opcional</small>
              </span>
              <textarea
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={2}
                maxLength={1000}
              />
            </label>

            {editing && seriesId && (
              <fieldset className="agv-set">
                <legend>Este compromisso se repete. Mudar ou apagar</legend>
                <div className="stg-seg" role="group">
                  <button
                    type="button"
                    className={scope === 'this' ? 'on' : ''}
                    aria-pressed={scope === 'this'}
                    onClick={() => setScope('this')}
                  >
                    Só este
                  </button>
                  <button
                    type="button"
                    className={scope === 'following' ? 'on' : ''}
                    aria-pressed={scope === 'following'}
                    onClick={() => setScope('following')}
                  >
                    Este e os próximos
                  </button>
                </div>
                <p className="agv-hint">O que já passou não muda.</p>
              </fieldset>
            )}
          </>
        )}

        {confirm && <p className="agv-confirm">{confirm}</p>}
        {error && (
          <p className="agv-error" role="alert">
            {error}
          </p>
        )}
      </form>
    </PagePanel>
  );
}
